import { IIdentificacaoBasica } from '../../../../interfaces/IIdentificacaoBasica';
import InformacaoBasica from '../../../../components/informacaoBasica/informacaoBasica';
import styles from './hastaPublicaIdBasica.module.scss';
import { BrLoading, BrSelect } from '@govbr-ds/react-components';
import { formatarCNPJOrCpf } from '../../../../utils/formatCNPJOrCPF';
import { formatarCEP } from '../../../../utils/formatCEP';
import { formatarTelefone } from '../../../../utils/formatTelefone';
import { ERegimesTributacao } from '../../../../enums/ERegimesTributacao';
import { useContext, useEffect, useState } from 'react';
import { DiefCfemContext } from '../../../../contexts/DiefCfemContext';
import { useBackEndRequests } from 'pages/HastaPublica/lib/backEndRequests';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { IRegimeTributacao } from 'interfaces/IRegimeTributacao';

interface HastaPublicaIdBasicaProps {
  declaranteData: IIdentificacaoBasica | undefined;
  isDeclaranteDataLoading: boolean;
  noBorder?: boolean;
  regimeTributacaoSalvo?: string;
}

interface IInfo {
  label: string;
  value: string | undefined;
  class?: string;
  format?: Function;
}

function HastaPublicaIdBasica({
  declaranteData,
  isDeclaranteDataLoading,
  noBorder = false,
  regimeTributacaoSalvo,
}: Readonly<HastaPublicaIdBasicaProps>) {
  const CAMPOS_IDENTIDADE: IInfo[] = [
    { label: 'Nome/Razão Social', value: declaranteData?.nomeRazaoSocial },
    { label: 'CPF/CNPJ', value: declaranteData?.cpfCnpj, format: formatarCNPJOrCpf },
    { label: 'Logradouro', value: declaranteData?.logradouro },
    { label: 'CEP', value: declaranteData?.cep, format: formatarCEP },
    {
      label: 'Município/UF',
      value: `${declaranteData?.municipio}/${declaranteData?.uf}`,
    },

    { label: 'E-mail', value: declaranteData?.email },
    { label: 'Telefone', value: declaranteData?.telefone, format: formatarTelefone },
  ];

  if (regimeTributacaoSalvo) {
    CAMPOS_IDENTIDADE.push({
      label: 'Regime Tributário',
      value: regimeTributacaoSalvo,
    });
  }

  const [regimeTributacao, setRegimeTributacao] = useState<ERegimesTributacao | null>(null);

  const containerStyle = noBorder ? styles.containerPrincipalNoBorder : styles.containerPrincipal;

  const { getRegimesTributacao } = useBackEndRequests();

  const context = useContext(DiefCfemContext);

  const {
    data: regimesTributacaoData,
    isLoading: isRegimesLoading,
    isError: isRegimesError,
    refetch: refetchRegimesTributacao,
  } = useQuery<IRegimeTributacao[], AxiosError>({
    queryKey: ['regimesTributacao', declaranteData?.cpfCnpj],
    queryFn: () => getRegimesTributacao(declaranteData!.cpfCnpj!),
    enabled: !!declaranteData?.cpfCnpj,
  });

  useEffect(() => {
    if (!isRegimesLoading && regimesTributacaoData?.length) {
      const regimeSalvo = context?.declaracaoHastaPublica?.regimeTributacao;

      if (declaranteData?.cpfCnpj?.length === 14) {
        if (regimeSalvo?.id) {
          setRegimeTributacao(regimeSalvo.id);
        } else {
          setRegimeTributacao(null);
        }
        return; //evita que o cpf rode lofo abaixo
      }

      if (declaranteData?.cpfCnpj?.length === 11 && !regimeTributacao) {
        const regimeOutros = regimesTributacaoData.find((r) => r.nome.toLowerCase().trim() === 'outros');

        if (regimeOutros) {
          setRegimeTributacao(regimeOutros.id);
          context?.setDeclaracaoHastaPublica({
            ...context.declaracaoHastaPublica,
            regimeTributacao: { id: regimeOutros.id, nome: regimeOutros.nome },
          });
        }
      }
    }
  }, [
    isRegimesLoading,
    regimesTributacaoData,
    declaranteData?.cpfCnpj,
    context?.declaracaoHastaPublica?.regimeTributacao,
  ]);

  const changeRegimeTributacao = (value: number) => {
    setRegimeTributacao(value);
    const regime = regimesTributacaoData?.find((r) => r.id === value);

    context?.setDeclaracaoHastaPublica({
      ...context.declaracaoHastaPublica,
      regimeTributacao: { id: value, nome: regime?.nome || '' },
    });
  };

  return (
    <div className={containerStyle}>
      <h2 className={'h4'}>Identificação Básica</h2>
      {declaranteData && !isDeclaranteDataLoading ? (
        <>
          <div className={styles.containerInterno}>
            {CAMPOS_IDENTIDADE.map((campo, index) => (
              <div key={index} className={campo.class ?? ''}>
                <InformacaoBasica
                  titulo={campo.label}
                  conteudo={campo.format && campo.value ? campo.format(campo.value) : campo.value || '-'}
                ></InformacaoBasica>
              </div>
            ))}
          </div>
          {!regimeTributacaoSalvo && (
            <BrSelect
              className={styles.selectRegimeTributacao}
              placeholder='Selecione um regime de tributação...'
              id='select-regime-tributacao'
              label='Regime de Tributação'
              options={regimesTributacaoData?.map((r) => ({ value: r.id, label: r.nome })) || []}
              key={regimeTributacao}
              value={regimeTributacao?.toString()}
              onChange={(value: string) => changeRegimeTributacao(parseInt(value))}
            />
          )}
        </>
      ) : isDeclaranteDataLoading ? (
        <div className={styles.tableLoading}>
          <BrLoading large label='Carregando...' />
        </div>
      ) : (
        <div className={styles.containerMensagem}>Não foi possível localizar os dados básicos.</div>
      )}
    </div>
  );
}

export default HastaPublicaIdBasica;
